/** Общая загрузка данных для страниц с таблицей аккаунтов. */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Account, ModelSummary, PhoneSummary } from '@wml-insta/shared';
import { isArchivedStatus } from '@wml-insta/shared';
import { api } from '../api/client.js';

/** Сколько строка остаётся в рабочем списке после перевода в архивный статус —
 *  чтобы успеть увидеть/отменить (Cmd+Z), а не ловить мгновенное исчезновение. */
const ARCHIVE_GRACE_MS = 20_000;

// Модульный стор грейса (id -> когда истекает): у каждой страницы свой
// инстанс хука, а таймер должен переживать переходы между страницами.
const graceStore = new Map<string, number>();
function activeGraceIds(): Set<string> {
  const now = Date.now();
  for (const [id, exp] of graceStore) if (exp <= now) graceStore.delete(id);
  return new Set(graceStore.keys());
}

/** Какой срез акков грузить: рабочие экраны — active, «Архив» — archived.
 *  Архив это ~3/4 базы, и на рабочих экранах он не нужен (22.09: панель
 *  качала 1.5МБ и долго рисовалась). */
export type AccountsScope = 'active' | 'archived' | 'all';

export function useAccountsData(scope: AccountsScope = 'active') {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [phones, setPhones] = useState<PhoneSummary[]>([]);
  const [models, setModels] = useState<ModelSummary[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    Promise.all([
      api<Account[]>(`/api/accounts?scope=${scope}`),
      api<PhoneSummary[]>('/api/phones'),
      api<ModelSummary[]>('/api/models'),
    ])
      .then(([a, p, m]) => {
        setAccounts(a);
        setPhones(p);
        setModels(m);
        setLoaded(true);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Не удалось загрузить данные');
        setLoaded(true);
      });
  }, [scope]);

  useEffect(reload, [reload]);

  // Акки, недавно переведённые в архивный статус: 20 сек висят в рабочих
  // списках (и не показываются в архиве), потом переезжают.
  const [archiveGrace, setArchiveGrace] = useState<Set<string>>(activeGraceIds);
  const graceTick = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleGraceRefresh = useCallback(() => {
    if (graceTick.current) clearTimeout(graceTick.current);
    const next = Math.min(...graceStore.values());
    if (!Number.isFinite(next)) return;
    graceTick.current = setTimeout(() => {
      setArchiveGrace(activeGraceIds());
      scheduleGraceRefresh();
    }, Math.max(50, next - Date.now()));
  }, []);
  useEffect(() => {
    scheduleGraceRefresh();
    return () => { if (graceTick.current) clearTimeout(graceTick.current); };
  }, [scheduleGraceRefresh]);

  const applyPatched = useCallback((updated: Account) => {
    setAccounts((prev) => {
      const old = prev.find((a) => a.id === updated.id);
      const becameArchived = old && !isArchivedStatus(old.status) && isArchivedStatus(updated.status);
      const unarchived = old && isArchivedStatus(old.status) && !isArchivedStatus(updated.status);
      if (becameArchived) {
        graceStore.set(updated.id, Date.now() + ARCHIVE_GRACE_MS);
        setArchiveGrace(activeGraceIds());
        scheduleGraceRefresh();
      } else if (unarchived) {
        // Вернули из архива (в т.ч. Cmd+Z) — грейс больше не нужен.
        graceStore.delete(updated.id);
        setArchiveGrace(activeGraceIds());
      }
      return prev.map((a) => (a.id === updated.id ? updated : a));
    });
  }, [scheduleGraceRefresh]);

  const applyCreated = useCallback((created: Account) => {
    setAccounts((prev) => [...prev, created]);
  }, []);

  const applyDeleted = useCallback((id: string) => {
    setAccounts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  return {
    accounts, phones, models, loaded,
    error, setError, archiveGrace,
    reload, applyPatched, applyCreated, applyDeleted,
  };
}
